import { inject, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { ActivityInterface } from '@vendasta/crm';
import { combineLatest, map, Observable, of, ReplaySubject, shareReplay, switchMap } from 'rxjs';
import { CrmInjectionToken, ObjectType, ObjectTypes } from '../../../tokens-and-interfaces';
import { PAGE_ROUTES } from '../../../constants';
import {
  CrmFieldService,
  CrmObjectService,
  PlatformExtensionFieldIds,
  StandardExternalIds,
  StandardIds,
} from '../../../shared-services';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs/operators';

export interface TaskPopoverAssociationType {
  name?: string;
  linkUrl?: string;
}

@Injectable()
export class AssociationPopoverService {
  private _activity: WritableSignal<ActivityInterface> = signal({});
  set activity(activity: ActivityInterface) {
    this._activity.set(activity);
  }
  get activity() {
    return this._activity();
  }
  set associatedObject(a: { crmObjectId: string; objectType: ObjectType }) {
    if (!ObjectTypes.includes(a.objectType)) {
      return;
    }
    this.objectType$$.next(a.objectType);
    this.crmObjectId$$.next(a.crmObjectId);
  }

  private readonly objectType$$ = new ReplaySubject<ObjectType>(1);
  protected objectType$: Observable<ObjectType> = this.objectType$$.asObservable();
  private readonly crmObjectId$$ = new ReplaySubject<string>(1);
  protected crmObjectId$: Observable<string> = this.crmObjectId$$.asObservable();

  protected primaryAccountGroupId$: Observable<string>;

  companies: Signal<TaskPopoverAssociationType[]>;
  contacts: Signal<TaskPopoverAssociationType[]>;
  opportunities: Signal<TaskPopoverAssociationType[]>;
  teamMembers: Signal<TaskPopoverAssociationType[]>;

  routePrefix: Signal<string>;

  protected readonly config = inject(CrmInjectionToken);
  protected readonly opportunityService = this.config.services?.opportunityService;
  protected readonly userService = this.config.services?.userService;

  constructor(
    private readonly fieldService: CrmFieldService,
    private readonly objectService: CrmObjectService,
  ) {
    this.routePrefix = toSignal(this.config.routePrefix$);

    this.primaryAccountGroupId$ = combineLatest([this.crmObjectId$, this.objectType$]).pipe(
      switchMap((v: [string, ObjectType]) => {
        const crmObjectId = v[0],
          crmObjectType = v[1];
        return this.objectService.getMultiObject(crmObjectType, [crmObjectId]);
      }),
      map((response) => response?.crmObjects?.[0]),
      map((crmObject) => {
        return (
          this.fieldService.getFieldValueFromCrmObject(crmObject, PlatformExtensionFieldIds.AccountGroupID)
            ?.stringValue ?? null
        );
      }),
      filter(Boolean),
      shareReplay(1),
    );

    const companies$ = toObservable(this._activity).pipe(
      switchMap((activity) => {
        const ids = activity?.associations
          ?.filter((a) => a.crmObjectType == 'Company')
          .map((a) => {
            return a.crmObjectId;
          });
        if (ids.length > 0) {
          return this.objectService.getMultiObject('Company', ids);
        } else {
          return of(null);
        }
      }),
      map((response) => (response?.crmObjects || []).filter((crmObject) => crmObject?.crmObjectId)),
      map((crmObjects) => {
        return crmObjects.map((o) => {
          return this.newTaskPopoverAssociation(
            this.fieldService.getFieldValueFromCrmObject(o, StandardIds.CompanyName)?.stringValue ?? '-',
            `${PAGE_ROUTES.COMPANY.ROOT}/${PAGE_ROUTES.COMPANY.SUBROUTES.PROFILE}`.replace(
              ':crmObjectId',
              o.crmObjectId || '',
            ),
          );
        });
      }),
    );

    this.companies = toSignal(companies$);

    const contacts$ = toObservable(this._activity).pipe(
      switchMap((activity) => {
        const ids = activity?.associations
          ?.filter((a) => a.crmObjectType == 'Contact')
          .map((a) => {
            return a.crmObjectId;
          });
        if (ids.length > 0) {
          return this.objectService.getMultiObject('Contact', ids);
        } else {
          return of(null);
        }
      }),
      map((response) => (response?.crmObjects || []).filter((crmObject) => crmObject?.crmObjectId)),
      map((crmObjects) => {
        return crmObjects.map((o) => {
          const displayName =
            (this.fieldService.getFieldValueFromCrmObject(o, StandardExternalIds.FirstName)?.stringValue ?? '-') +
            ' ' +
            (this.fieldService.getFieldValueFromCrmObject(o, StandardExternalIds.LastName)?.stringValue ?? ' ');
          return this.newTaskPopoverAssociation(
            displayName,
            `${PAGE_ROUTES.CONTACT.ROOT}/${PAGE_ROUTES.CONTACT.SUBROUTES.PROFILE}`.replace(
              ':crmObjectId',
              o.crmObjectId || '',
            ),
          );
        });
      }),
    );

    this.contacts = toSignal(contacts$);

    const opportunities$ = combineLatest([toObservable(this._activity), this.primaryAccountGroupId$]).pipe(
      switchMap((v: [ActivityInterface, string]) => {
        const activity = v[0],
          accountGroupId = v[1];
        const ids = activity?.associations
          ?.filter((a) => a.crmObjectType == 'Opportunity')
          .map((a) => {
            return a.crmObjectId;
          });
        if (ids.length > 0) {
          this.opportunityService?.setAccountGroupId(accountGroupId);
          return this.opportunityService?.getMultiOpportunity(ids);
        } else {
          return of(null);
        }
      }),
      map((response) => {
        return response?.map((o) => {
          return this.newTaskPopoverAssociation(o?.name ?? '-', '');
        });
      }),
    );

    this.opportunities = toSignal(opportunities$);

    const userIds$ = toObservable(this._activity).pipe(
      switchMap((activity: ActivityInterface) => {
        const ids = activity?.associations
          ?.filter((a) => a.crmObjectType == 'User')
          .map((a) => {
            return a.crmObjectId;
          });
        if (ids.length > 0) {
          return this.userService?.getMultiUsers(ids);
        } else {
          return of(null);
        }
      }),
      map((users) => (users || []).filter((user) => user?.userId)),
      map((users) => {
        return users?.map((o) => {
          return this.newTaskPopoverAssociation(o?.displayName ?? '-', '');
        });
      }),
    );

    this.teamMembers = toSignal(userIds$);
  }

  private newTaskPopoverAssociation(name: string, link: string): TaskPopoverAssociationType {
    return {
      name: name,
      linkUrl: link,
    };
  }
}
